import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Minus, Plus, ShoppingBag, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { amazonUrl, formatPrice } from "@/data/books";

export default function CartDrawer() {
  const { items, subtotal, open, setOpen, setQty, remove } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[95] bg-walnut/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[100] h-full w-full max-w-md bg-cream border-l border-sand flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-sand">
              <h2 className="font-display font-bold text-xl text-walnut flex items-center gap-2">
                <ShoppingBag size={20} className="text-terracotta" /> Your basket
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full border border-sand flex items-center justify-center text-walnutsoft hover:text-walnut hover:border-walnut transition-colors"
                aria-label="Close basket"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5" data-lenis-prevent>
              {items.length === 0 ? (
                <div data-testid="cart-empty-state" className="h-full flex flex-col items-center justify-center text-center gap-4 py-16">
                  <div className="w-16 h-16 rounded-full bg-creamdark flex items-center justify-center">
                    <ShoppingBag size={24} className="text-walnutmuted" />
                  </div>
                  <p className="font-display font-semibold text-lg text-walnut">Your basket is empty</p>
                  <p className="text-sm text-walnutsoft max-w-[240px]">
                    Add a Bailey adventure and check out securely on Amazon UK.
                  </p>
                  <Link
                    to="/books"
                    data-testid="cart-browse-books-link"
                    onClick={() => setOpen(false)}
                    className="mt-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm font-semibold hover:bg-terracottadark transition-colors"
                  >
                    Browse the books
                  </Link>
                </div>
              ) : (
                <ul className="flex flex-col gap-5">
                  {items.map(({ book, qty }) => (
                    <motion.li
                      key={book.slug}
                      data-testid={`cart-item-${book.slug}`}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex gap-4 bg-white/70 border border-sand rounded-2xl p-3"
                    >
                      <img src={book.cover} alt={book.title} className="w-16 h-20 object-cover rounded-lg shadow-sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display font-semibold text-sm text-walnut leading-snug">{book.title}</p>
                          <button
                            data-testid={`cart-remove-${book.slug}`}
                            onClick={() => remove(book.slug)}
                            className="text-walnutmuted hover:text-terracotta transition-colors"
                            aria-label={`Remove ${book.title}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-walnutmuted mt-0.5">{book.format} · {book.ages}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2 border border-sand rounded-full px-1 py-0.5">
                            <button data-testid={`cart-qty-minus-${book.slug}`} onClick={() => setQty(book.slug, qty - 1)} className="w-6 h-6 flex items-center justify-center text-walnutsoft hover:text-walnut" aria-label="Decrease"><Minus size={12} /></button>
                            <span className="text-xs font-bold w-4 text-center">{qty}</span>
                            <button data-testid={`cart-qty-plus-${book.slug}`} onClick={() => setQty(book.slug, qty + 1)} className="w-6 h-6 flex items-center justify-center text-walnutsoft hover:text-walnut" aria-label="Increase"><Plus size={12} /></button>
                          </div>
                          <span className="font-display font-bold text-sm text-walnut">{formatPrice(book.price * qty)}</span>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-sand px-6 py-5 bg-creamdark/60">
                {items.length >= 3 && (
                  <p data-testid="cart-bundle-note" className="text-xs font-semibold text-sage bg-sagecream border border-sage/20 rounded-xl px-3 py-2 mb-4">
                    Bundle bonus — ask about a free activity pack with 3+ books at author events.
                  </p>
                )}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-walnutsoft">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-display font-extrabold text-2xl text-walnut">{formatPrice(subtotal)}</span>
                </div>
                <p className="text-xs text-walnutmuted mb-4">Checkout is completed securely on Amazon UK — Prime delivery available.</p>
                <div className="flex flex-col gap-2">
                  {items.map(({ book }) => (
                    <a
                      key={book.slug}
                      data-testid={`cart-buy-amazon-${book.slug}`}
                      href={amazonUrl(book)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-full bg-walnut text-cream px-5 py-3 text-sm font-semibold hover:bg-terracotta transition-colors duration-300"
                    >
                      <span className="truncate mr-3">Buy “{book.title}”</span>
                      <span className="flex items-center gap-1.5 shrink-0 text-xs opacity-90">
                        Amazon UK <ExternalLink size={13} />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
